import { ClockIcon } from '@heroicons/react/24/outline';
import type { ActiveFilter } from '@oysterjs/ui/search-filter';
import {
  type FilterDefinition,
  type FilterGeneratorContext,
  type FilterSuggestion,
  type SerializedFilter
} from '@oysterjs/ui/search-filter';

import { CustomFormStatus, type CustomFormListFieldsFragment } from '~/api/graphql';
import {
  BaseDateFilter,
  BaseSearchFilter,
  BaseSharedWithMeFilter,
  BaseTeamFilter,
  BaseUserFilter,
  BaseVisibilityFilter,
  type FilterViewer,
  getSinceDate,
  matchesVisibilityFilter
} from '~/api/data/filter-base';

export type CustomFormFilter =
  | 'search'
  | 'user'
  | 'date'
  | 'status'
  | 'visibility'
  | 'sharedWithMe'
  | 'team';

function getCustomFormStatusTitle(status: CustomFormStatus): string {
  switch (status) {
    case CustomFormStatus.Ready:
      return 'Ready';
    case CustomFormStatus.Queued:
      return 'Queued';
    case CustomFormStatus.Processing:
      return 'Processing';
    case CustomFormStatus.Failed:
      return 'Failed';
  }
}

/** Display name for the form creator (GraphQL `createdBy`, with loader fallback). */
export function customFormCreatorLabel(
  form: CustomFormListFieldsFragment,
  userNameById: (id: string) => string
): string {
  const name = form.createdBy?.name?.trim();
  if (name) return name;
  const id = form.createdBy?.id;
  return id ? userNameById(id) : '';
}

function formMatchesFilter(
  form: CustomFormListFieldsFragment,
  filter: ActiveFilter<CustomFormFilter>,
  userNameById: (id: string) => string,
  viewer: FilterViewer
): boolean {
  const visibilityMatch = matchesVisibilityFilter(form, filter, viewer);
  if (visibilityMatch !== null) return visibilityMatch;
  switch (filter.type) {
    case 'search': {
      const term = (filter.value as string).toLowerCase();
      return (
        form.title.toLowerCase().includes(term) ||
        customFormCreatorLabel(form, userNameById).toLowerCase().includes(term)
      );
    }
    case 'user':
      return form.createdBy?.id === filter.value;
    case 'date': {
      const since = getSinceDate(filter.value as string);
      if (since == null) return true;
      return form.createdAt != null && new Date(form.createdAt) >= since;
    }
    case 'status':
      return form.status === filter.value;
    default:
      return true;
  }
}

export function filterCustomForms(
  forms: CustomFormListFieldsFragment[],
  activeFilters: ActiveFilter<CustomFormFilter>[],
  userNameById: (id: string) => string,
  viewer: FilterViewer
): CustomFormListFieldsFragment[] {
  if (activeFilters.length === 0) return forms;
  return forms.filter((f) =>
    activeFilters.every((fl) => formMatchesFilter(f, fl, userNameById, viewer))
  );
}

export class CustomFormUserFilter extends BaseUserFilter<CustomFormFilter> {
  constructor() {
    super('user');
  }
}

export class CustomFormDateFilter extends BaseDateFilter<CustomFormFilter> {
  constructor() {
    super('date');
  }
}

export class CustomFormSearchFilter extends BaseSearchFilter<CustomFormFilter> {
  constructor() {
    super('search');
  }
}

export class CustomFormVisibilityFilter extends BaseVisibilityFilter<CustomFormFilter> {
  constructor() {
    super('visibility');
  }
}

export class CustomFormSharedWithMeFilter extends BaseSharedWithMeFilter<CustomFormFilter> {
  constructor() {
    super('sharedWithMe');
  }
}

export class CustomFormTeamFilter extends BaseTeamFilter<CustomFormFilter> {
  constructor() {
    super('team');
  }
}

export class CustomFormStatusFilter implements FilterDefinition<CustomFormFilter> {
  readonly type = 'status' as const;
  readonly icon = ClockIcon;
  readonly priority = 0;
  readonly isUnique = false;

  generate({ query }: FilterGeneratorContext<CustomFormFilter>): FilterSuggestion[] {
    const q = query.toLowerCase();
    return Object.values(CustomFormStatus)
      .filter(
        (s) =>
          getCustomFormStatusTitle(s).toLowerCase().includes(q) ||
          s.toLowerCase().includes(q) ||
          'status'.includes(q)
      )
      .map((s) => ({
        value: s,
        displayLabel: 'Status is',
        displayValue: getCustomFormStatusTitle(s)
      }));
  }

  deserialize(s: SerializedFilter<CustomFormFilter>): ActiveFilter<CustomFormFilter> | null {
    if (!Object.values(CustomFormStatus).includes(s.value as CustomFormStatus)) {
      return null;
    }
    return {
      id: `status-${s.value}`,
      type: this.type,
      value: s.value,
      displayLabel: 'Status is',
      displayValue: s.displayValue
    };
  }
}

export const createCustomFormFilterDefinitions = (): FilterDefinition<CustomFormFilter>[] => [
  new CustomFormStatusFilter(),
  new CustomFormUserFilter(),
  new CustomFormDateFilter(),
  new CustomFormSearchFilter(),
  new CustomFormVisibilityFilter(),
  new CustomFormSharedWithMeFilter(),
  new CustomFormTeamFilter()
];
